/**
 * Web USB API transport for ESC/POS thermal printers.
 *
 * Uses (navigator as any).usb to communicate with USB-connected thermal printers.
 * Supported vendors: Epson, Star Micronics, Bixolon, TVS, WeP (common Indian POS printers).
 */
import type { PrinterTransport, PrinterDeviceInfo } from '../types';

// Common thermal printer USB vendor IDs
const KNOWN_VENDORS: { vendorId: number; name: string }[] = [
  { vendorId: 0x04b8, name: 'Epson' },
  { vendorId: 0x0519, name: 'Star Micronics' },
  { vendorId: 0x1504, name: 'Bixolon' },
  { vendorId: 0x0fe6, name: 'TVS Electronics' },
  { vendorId: 0x0dd4, name: 'Custom' },
  { vendorId: 0x0416, name: 'WeP' },
  { vendorId: 0x0483, name: 'STMicroelectronics' },
  { vendorId: 0x1fc9, name: 'NXP (generic)' },
];

export class UsbTransport implements PrinterTransport {
  readonly type = 'usb' as const;
  private device: any = null;
  private endpoint: any = null;
  private connected = false;

  /** Check if Web USB API is available in this browser. */
  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'usb' in navigator;
  }

  /** Request user to select a USB printer and connect. */
  async connect(): Promise<void> {
    if (!UsbTransport.isSupported()) {
      throw new Error('Web USB API is not supported in this browser');
    }

    try {
      // Request device with vendor filters
      this.device = await (navigator as any).usb.requestDevice({
        filters: KNOWN_VENDORS.map((v) => ({ vendorId: v.vendorId })),
      });

      await this.device.open();

      // Select first configuration if not already selected
      if (this.device.configuration === null) {
        await this.device.selectConfiguration(1);
      }

      // Find the first interface with a bulk OUT endpoint
      const iface = this.device.configuration?.interfaces.find((i: any) =>
        i.alternate.endpoints.some((e: any) => e.direction === 'out' && e.type === 'bulk'),
      );

      if (!iface) {
        throw new Error('No suitable USB interface found on this device');
      }

      await this.device.claimInterface(iface.interfaceNumber);

      this.endpoint = iface.alternate.endpoints.find(
        (e: any) => e.direction === 'out' && e.type === 'bulk',
      ) ?? null;

      if (!this.endpoint) {
        throw new Error('No bulk OUT endpoint found');
      }

      this.connected = true;

      // Listen for disconnect
      (navigator as any).usb.addEventListener('disconnect', this.handleDisconnect);
    } catch (err) {
      this.connected = false;
      this.device = null;
      this.endpoint = null;
      if (err instanceof DOMException && err.name === 'NotFoundError') {
        throw new Error('No printer selected');
      }
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    (navigator as any).usb.removeEventListener('disconnect', this.handleDisconnect);
    if (this.device) {
      try {
        await this.device.close();
      } catch {
        // Ignore close errors
      }
    }
    this.device = null;
    this.endpoint = null;
    this.connected = false;
  }

  async write(data: Uint8Array): Promise<void> {
    if (!this.device || !this.endpoint || !this.connected) {
      throw new Error('Printer not connected');
    }

    // Send in chunks of 64 bytes (common USB packet size for printers)
    const chunkSize = 64;
    for (let offset = 0; offset < data.length; offset += chunkSize) {
      const chunk = data.slice(offset, offset + chunkSize);
      await this.device.transferOut(this.endpoint.endpointNumber, chunk);
    }
  }

  isConnected(): boolean {
    return this.connected && this.device !== null;
  }

  getDeviceInfo(): PrinterDeviceInfo | null {
    if (!this.device) return null;
    const vendor = KNOWN_VENDORS.find((v) => v.vendorId === this.device?.vendorId);
    return {
      name: this.device.productName ?? vendor?.name ?? 'USB Printer',
      type: 'usb',
      vendorId: this.device.vendorId,
      productId: this.device.productId,
    };
  }

  private handleDisconnect = (event: any) => {
    if (event.device === this.device) {
      this.connected = false;
      this.device = null;
      this.endpoint = null;
    }
  };
}
